import styled, { css, keyframes } from "styled-components";
import { tokens as T, surface, hairline } from "../../styles/designTokens";
import {
  EntityCard, Group, GroupLabel, Hero, IconTile, LiveDot, LoadMore, Meter, Page, Row, SearchField,
  SectionHeader, Segmented, Sheet, StatStrip, TopBar, Widget, tokens as activity,
} from "../activity-logs/styles";

/**
 * A tela usa os componentes e a disposição de Atividades (activity-logs/styles),
 * com o conteúdo refinado aqui: fonte do sistema, números tabulares, hierarquia por
 * peso e opacidade, e cores vivas só em áreas pequenas, no espírito do iOS. Tudo
 * fica restrito a esta tela; os estilos de Atividades não mudam.
 */

export const SANS = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', 'Segoe UI', sans-serif";

const text2 = ({ theme }) => activity.label2(theme);
const fill = ({ theme }) => surface(theme, 2);
const borderStrong = ({ theme }) => hairline(theme, true);

const refined = css`
  font-family: ${SANS};
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;

  ${TopBar} {
    .eyebrow {
      font-family: ${SANS};
      font-size: 0.8em;
      font-weight: 500;
      text-transform: none;
      letter-spacing: 0;
      opacity: 0.5;
      margin-bottom: 2px;
    }
    h1 { font-size: 2em; font-weight: 700; letter-spacing: -0.03em; }
    .meta { font-size: 0.8em; }
    .update-tag { font-family: ${SANS}; font-size: 1em; opacity: 0.5; }
  }

  ${LiveDot} {
    font-family: ${SANS};
    font-size: 1em;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    gap: 6px;
    color: ${text2};
    &::before { width: 6px; height: 6px; box-shadow: none; animation: none; opacity: 0.8; }
  }

  ${Segmented} {
    padding: 2px;
    border: none;
    border-radius: 10px;
    background: ${fill};

    .thumb {
      top: 2px;
      bottom: 2px;
      left: 2px;
      border-radius: 8px;
      background: ${({ theme }) => (activity.dk(theme) ? "rgba(255, 255, 255, 0.13)" : "#fff")};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
    }

    button {
      font-family: ${SANS};
      font-size: 0.8em;
      font-weight: 500;
      text-transform: none;
      letter-spacing: -0.01em;
      opacity: 0.6;

      &.active { opacity: 1; color: ${({ theme }) => theme.text}; }
      .count, &.active .count { padding: 0; background: transparent; color: inherit; opacity: 0.5; }
    }
  }

  ${SearchField} {
    border: none;
    border-radius: 10px;
    background: ${fill};
    &:focus-within { border-color: transparent; box-shadow: 0 0 0 2px ${borderStrong}; }
  }

  ${Widget} {
    &:hover { border-color: ${borderStrong}; transform: none; }

    .w-head {
      font-size: 0.82em;
      font-weight: 500;
      color: ${text2};
      > * { opacity: 1; }
      svg { color: inherit; opacity: 0.7; }
    }
    .w-value {
      font-family: ${SANS};
      font-size: clamp(1.3em, 1.9vw, 1.7em);
      font-weight: 600;
      letter-spacing: -0.03em;
    }
    .w-foot { font-family: ${SANS}; font-size: 0.76em; color: ${text2}; opacity: 1; }
    .w-title { font-size: 1.02em; letter-spacing: -0.015em; }
    .w-sub { font-size: 0.8em; }
    .w-eyebrow {
      font-family: ${SANS};
      font-size: 0.76em;
      font-weight: 400;
      text-transform: none;
      letter-spacing: 0;
      color: ${text2};
      opacity: 1;
    }
  }

  ${SectionHeader} {
    h2 { font-size: 1em; opacity: 1; letter-spacing: -0.015em; }
    .caption { font-size: 0.8em; }
    button.link {
      font-family: ${SANS};
      font-size: 0.82em;
      font-weight: 400;
      text-transform: none;
      letter-spacing: 0;
    }
  }

  ${Group} { border-radius: ${T.radiusLg}; }

  ${Row} {
    .title { font-size: 0.92em; }
    .subtitle { font-size: 0.8em; }
    .trailing { font-family: ${SANS}; font-size: 0.86em; }
    .value { font-family: ${SANS}; font-weight: 500; letter-spacing: -0.01em; opacity: 1; }
  }

  ${EntityCard} {
    &::before { display: none; }
    &:hover { border-color: ${borderStrong}; box-shadow: none; transform: none; }
    .name { font-size: 0.95em; letter-spacing: -0.012em; }
    .sub { font-family: ${SANS}; font-size: 0.78em; color: ${text2}; opacity: 1; }
    .stats .s-value { font-family: ${SANS}; font-size: 1.1em; letter-spacing: -0.02em; }
    .stats .s-label {
      font-family: ${SANS};
      font-size: 0.74em;
      font-weight: 400;
      text-transform: none;
      letter-spacing: 0;
      color: ${text2};
      opacity: 1;
    }
    .foot { font-size: 0.78em; }
  }

  ${IconTile} { border: none; }

  ${Meter} { height: 3px; }

  ${GroupLabel} {
    font-family: ${SANS};
    font-size: 0.78em;
    text-transform: none;
    letter-spacing: 0;
  }

  ${StatStrip} {
    .v { font-family: ${SANS}; font-size: 1.05em; letter-spacing: -0.02em; }
    .l {
      font-family: ${SANS};
      font-size: 0.74em;
      font-weight: 400;
      text-transform: none;
      letter-spacing: 0;
      color: ${text2};
      opacity: 1;
    }
  }

  ${Hero} {
    h3 { font-weight: 700; }
    .hero-sub { font-family: ${SANS}; font-size: 0.8em; }
  }

  ${LoadMore} {
    font-family: ${SANS};
    font-size: 0.82em;
    text-transform: none;
    letter-spacing: 0;
    color: ${text2};
    &:hover { background: ${fill}; border-color: ${borderStrong}; }
  }
`;

export const CreditsPage = styled(Page)`
  ${refined};
  &::before { opacity: 0.4; }
`;

export const CreditsSheet = styled(Sheet)`
  ${refined};
  &::before { display: none; }
`;

export const ActionButton = styled.button`
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 10px;
  font-family: ${SANS};
  font-size: 0.82em;
  font-weight: 500;
  letter-spacing: -0.01em;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  transition: background ${T.durBase} ${T.easeOut}, transform ${T.durBase} ${T.easeOut};
  color: ${({ $danger }) => ($danger ? "#ef4444" : T.brand)};
  background: ${({ $danger }) => ($danger ? "transparent" : T.brandSoft)};

  svg { opacity: 0.75; }
  &:hover:not(:disabled) { background: ${({ $danger }) => ($danger ? "rgba(239, 68, 68, 0.08)" : T.brandRing)}; }
  &:active:not(:disabled) { transform: scale(0.97); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const FilterField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  background: ${fill};
  box-shadow: ${({ $active, theme }) => ($active ? `inset 0 0 0 1px ${hairline(theme, true)}` : "none")};
  color: ${text2};
  font-family: ${SANS};
  font-size: 0.82em;

  .f-label { font-size: 0.95em; }

  input, select {
    border: none;
    outline: none;
    background: transparent;
    color: ${({ theme }) => theme.text};
    font-family: inherit;
    font-size: inherit;
    cursor: pointer;
    color-scheme: ${({ theme }) => (activity.dk(theme) ? "dark" : "light")};
  }

  select { max-width: 200px; text-overflow: ellipsis; }
  option { background: ${({ theme }) => activity.solid(theme)}; color: ${({ theme }) => theme.text}; }
`;

export const Notice = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 9px;
  border-radius: 12px;
  font-size: 0.82em;
  color: ${text2};
  background: ${fill};

  svg { flex-shrink: 0; opacity: 0.7; }
`;

// Hierarquia do valor: a parte inteira é o destaque; moeda e centavos, discretos
export const MoneyText = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .cur { font-size: 0.6em; font-weight: 500; opacity: 0.5; margin-right: 0.3em; letter-spacing: 0; }
  .dec { font-size: 0.68em; font-weight: 500; opacity: 0.45; letter-spacing: -0.01em; }
`;

// Tipo de crédito discreto: texto secundário com um ponto na cor do imposto
export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  font-family: ${SANS};
  font-size: 0.86em;
  font-weight: 400;
  color: ${text2};

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
  }
`;

export const AvatarBox = styled.div`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  border-radius: ${({ $size }) => Math.round($size * 0.26)}px;
  background: ${({ $color }) => `${$color}1a`};
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $size }) => Math.round($size * 0.36)}px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

// O .title das linhas é flex, então as reticências precisam de um elemento próprio
export const TruncatedText = styled.span`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Com 2 colunas, a composição ocupa a linha inteira em vez de deixar um buraco
export const CompositionWidget = styled(Widget)`
  @media (max-width: 1200px) { grid-column: span 2; }
  @media (max-width: 640px) { grid-column: span 1; }
`;

// Mapa de calor ano × mês
export const PeriodMap = styled.div`
  display: grid;
  grid-template-columns: 38px repeat(12, minmax(0, 1fr));
  gap: 4px;
  font-size: 0.72em;
  color: ${text2};

  .head { text-align: center; padding-bottom: 2px; }
  .year { display: flex; align-items: center; }

  .cell {
    height: 30px;
    padding: 0;
    border: none;
    border-radius: 7px;
    position: relative;
    overflow: hidden;
    background: ${fill};
    transition: transform ${T.durFast} ${T.easeOut};

    .fill { position: absolute; inset: 0; background: ${T.brand}; }

    &:not(:disabled) { cursor: pointer; }
    &:not(:disabled):hover { transform: scale(1.06); }
  }
`;

// Resumo do período, encostado na base do cartão do mapa
export const PeriodStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => hairline(theme)};

  > div { display: flex; flex-direction: column; gap: 3px; min-width: 0; }

  .v { font-size: 1.05em; font-weight: 600; letter-spacing: -0.02em; }
  .l {
    font-size: 0.76em;
    color: ${text2};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const TaxBar = styled.div`
  display: flex;
  height: 4px;
  gap: 2px;
  border-radius: 2px;
  overflow: hidden;

  span { display: block; min-width: 2px; }
`;

// Volta no estilo da navigation bar do iOS
export const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 1px;
  margin: 0 0 4px -5px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: ${SANS};
  font-size: 0.86em;
  font-weight: 400;
  max-width: 340px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${T.brand};

  svg { font-size: 1.35em; flex-shrink: 0; }
  &:hover { opacity: 0.7; }
`;

// Célula de grade que acompanha os mesmos pontos de quebra do WidgetGrid
export const SpanCell = styled.div`
  grid-column: span ${({ $span }) => $span};
  min-width: 0;

  @media (max-width: 1200px) { grid-column: span ${({ $span }) => Math.min($span, 2)}; }
  @media (max-width: 640px) { grid-column: span 1; }
`;

// Base × alíquota = crédito: a composição do valor em uma linha
export const Equation = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "10px" : "22px")};
  flex-wrap: wrap;
  margin-top: ${({ $compact }) => ($compact ? "16px" : "6px")};
  padding: ${({ $compact }) => ($compact ? "12px 14px" : "0")};
  border-radius: 12px;
  background: ${({ $compact, theme }) => ($compact ? surface(theme, 1) : "transparent")};

  > div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }

  .l { font-size: ${({ $compact }) => ($compact ? "0.72em" : "0.76em")}; color: ${text2}; }
  .v {
    font-size: ${({ $compact }) => ($compact ? "0.92em" : "1.5em")};
    font-weight: 600;
    letter-spacing: -0.025em;
    white-space: nowrap;
  }
  .result .v { color: ${({ $color }) => $color}; }
  .op { font-size: ${({ $compact }) => ($compact ? "0.9em" : "1.2em")}; color: ${text2}; opacity: 0.6; }
`;

export const StepNumber = styled.span`
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78em;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}1a`};
`;

// Cartões de destaque (padrão da tela de armazenamento do iOS)
export const HeaderLink = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 1px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: ${SANS};
  font-size: 0.82em;
  color: ${T.brand};

  svg { font-size: 1.1em; }
  &:hover { opacity: 0.7; }
`;

export const TopHeadline = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 14px;

  .n { font-size: 1.5em; font-weight: 600; letter-spacing: -0.03em; line-height: 1.1; }
  .pct { font-size: 0.62em; font-weight: 500; letter-spacing: 0; opacity: 0.5; margin-left: 1px; }
  .l { font-size: 0.8em; color: ${text2}; }
`;

export const StorageBar = styled.div`
  display: flex;
  height: 4px;
  gap: 2px;
  margin-top: 10px;
  border-radius: 2px;
  overflow: hidden;

  span { display: block; flex-basis: 0; min-width: 3px; opacity: 0.72; }
  .rest { background: ${fill}; opacity: 1; }
`;

export const TopRow = styled.button`
  position: relative;
  width: calc(100% + 16px);
  margin: 0 -8px;
  padding: 11px 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: ${({ theme }) => theme.text};
  font-family: inherit;
  font-size: inherit;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;
  transition: background ${T.durBase} ${T.easeOut};

  /* Separador alinhado ao texto, como nas listas do iOS */
  & + &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 52px;
    right: 8px;
    height: 1px;
    background: ${({ theme }) => hairline(theme)};
    opacity: 0.7;
    transition: opacity ${T.durBase} ${T.easeOut};
  }

  /* Ícone esmaecido em repouso; a cor plena fica para o foco */
  .lead { flex-shrink: 0; opacity: 0.82; transition: opacity ${T.durBase} ${T.easeOut}; }

  &:hover, &:focus-visible {
    background: ${({ theme }) => surface(theme, 1)};
    outline: none;
    .lead { opacity: 1; }
    .chevron { opacity: 0.45; transform: translateX(0); }
  }
  &:hover::before, &:hover + &::before { opacity: 0; }
  &:active { background: ${fill}; }

  .main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
  .title {
    font-size: 0.9em;
    font-weight: 500;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sub {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.76em;
    color: ${text2};
    white-space: nowrap;
    overflow: hidden;
  }
  ${Tag}::before { width: 5px; height: 5px; opacity: 0.85; }

  .end { flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-end; gap: 3px; }
  .value { font-size: 0.9em; font-weight: 500; letter-spacing: -0.015em; }
  .value .cur { font-size: 0.8em; opacity: 0.5; margin-right: 0.25em; letter-spacing: 0; }
  .value .dec { opacity: 0.5; }
  .share { font-size: 0.74em; color: ${text2}; opacity: 0.85; }

  /* Coluna estreita: sem seta, para sobrar espaço ao valor */
  ${({ $compact }) => $compact && css`
    gap: 10px;
    .chevron { display: none; }
    & + &::before { left: 46px; }
  `}

  /* Seta só no foco: uma coluna de setas em repouso vira ruído */
  .chevron {
    flex-shrink: 0;
    margin-left: -6px;
    color: ${text2};
    opacity: 0;
    transform: translateX(-3px);
    transition: opacity ${T.durBase} ${T.easeOut}, transform ${T.durBase} ${T.easeOut};
  }
`;

// Mosaico de categorias: blocos neutros, com a cor só no ícone e na barra
export const Bento = styled.div`
  flex: 1;
  display: flex;
  gap: 6px;
  min-height: 214px;
  margin-top: 14px;

  > button { flex: 1 1 0; min-width: 0; }
  .side { flex: 1.7 1 0; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
  .side > button { flex: 1 1 0; }
  .base { flex: 1 1 0; display: flex; gap: 6px; min-height: 0; }
  .base > button { flex: 1 1 0; min-width: 0; }

  @media (max-width: 640px) {
    flex-direction: column;
    > button { flex: none; min-height: 132px; }
    .side > button { flex: none; min-height: 112px; }
    .base { flex: none; }
    .base > button { min-height: 96px; }
  }
`;

const bentoSizes = {
  large: { pad: "14px", pct: "2.1em", label: "0.86em", amount: "1em", icon: "1.1em", gap: "5px" },
  medium: { pad: "12px 14px", pct: "1.5em", label: "0.82em", amount: "0.88em", icon: "1em", gap: "3px" },
  small: { pad: "10px 11px", pct: "1.12em", label: "0.72em", amount: "0.72em", icon: "0.88em", gap: "2px" },
};

const growLevel = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

export const BentoTile = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  padding: ${({ $size }) => bentoSizes[$size].pad};
  border: none;
  border-radius: 12px;
  background: ${({ theme }) => (activity.dk(theme) ? "rgba(255, 255, 255, 0.035)" : "rgba(15, 23, 42, 0.025)")};
  color: ${({ theme }) => theme.text};
  font-family: inherit;
  font-size: inherit;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;
  transition: background ${T.durBase} ${T.easeOut}, transform ${T.durBase} ${T.easeOut};

  &:hover { background: ${({ theme }) => (activity.dk(theme) ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.05)")}; }
  &:focus-visible { outline: 2px solid ${borderStrong}; outline-offset: 1px; }
  &:active { transform: scale(0.985); }

  .top { display: flex; justify-content: space-between; align-items: flex-start; gap: 6px; }

  .pct {
    font-size: ${({ $size }) => bentoSizes[$size].pct};
    font-weight: 600;
    letter-spacing: -0.035em;
    line-height: 1.05;

    small { font-size: 0.56em; font-weight: 500; letter-spacing: 0; opacity: 0.45; margin-left: 1px; }
  }

  .icon {
    display: flex;
    flex-shrink: 0;
    font-size: ${({ $size }) => bentoSizes[$size].icon};
    color: ${({ $color }) => $color};
  }

  .bottom { display: flex; flex-direction: column; gap: ${({ $size }) => bentoSizes[$size].gap}; min-width: 0; }

  .label {
    font-size: ${({ $size }) => bentoSizes[$size].label};
    font-weight: 500;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .amount {
    font-size: ${({ $size }) => bentoSizes[$size].amount};
    color: ${text2};
    letter-spacing: -0.01em;

    .cur { font-size: 0.82em; opacity: 0.7; }
    .dec { font-size: 0.92em; opacity: 0.7; }
  }

  /* O tamanho do bloco segue o ranking; a barra mostra a parte real */
  .track {
    display: block;
    height: 3px;
    margin-top: ${({ $size }) => ($size === "small" ? "5px" : "7px")};
    border-radius: 2px;
    overflow: hidden;
    background: ${fill};
  }

  .level {
    display: block;
    height: 100%;
    min-width: 3px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
    transform-origin: left;
    animation: ${growLevel} 0.7s ${T.easeOut} both;

    @media (prefers-reduced-motion: reduce) { animation: none; }
  }
`;

// Diretório de fornecedores: cards sem borda colorida, no espírito da Carteira do iOS
export const SupplierGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 12px;
  margin-top: 10px;
`;

const cardRise = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const DirectoryCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: 18px;
  background: ${({ theme }) => surface(theme, 0)};
  color: ${({ theme }) => theme.text};
  font-family: inherit;
  font-size: inherit;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;
  transition: background ${T.durBase} ${T.easeOut}, border-color ${T.durBase} ${T.easeOut}, transform ${T.durBase} ${T.easeOut};
  animation: ${cardRise} 0.4s ${T.easeOut} both;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  @media (prefers-reduced-motion: reduce) { animation: none; }

  &:hover { background: ${({ theme }) => surface(theme, 1)}; border-color: ${borderStrong}; }
  &:focus-visible { outline: 2px solid ${borderStrong}; outline-offset: 2px; }
  &:active { transform: scale(0.985); }

  /* Em repouso o conteúdo fica atenuado; acende no hover. Fica nos filhos para não brigar com a animação de entrada */
  & > .head, & > .body { opacity: 0.6; transition: opacity ${T.durBase} ${T.easeOut}; }
  &:hover > .head, &:hover > .body,
  &:focus-visible > .head, &:focus-visible > .body { opacity: 1; }
  @media (hover: none) { & > .head, & > .body { opacity: 1; } }

  .head { display: flex; align-items: flex-start; gap: 11px; min-width: 0; }
  .who { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }

  .name {
    font-size: 0.9em;
    font-weight: 600;
    letter-spacing: -0.012em;
    line-height: 1.25;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .id { font-size: 0.74em; color: ${text2}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  /* Empurra o conteúdo para a base: os valores alinham mesmo com nomes de alturas diferentes */
  .body { margin-top: auto; display: flex; flex-direction: column; min-width: 0; }

  .amount { font-size: 1.4em; font-weight: 600; letter-spacing: -0.03em; line-height: 1.15; }
  .meta { margin-top: 3px; font-size: 0.76em; color: ${text2}; }
  .bar { margin-top: 12px; height: 3px; }
  .taxes { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 3px 10px; font-size: 0.82em; }
`;

// Barra horizontal com o nome por dentro; a cor preenche só a parte proporcional
export const BarListRow = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 106px 44px;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.text};
  font-family: inherit;
  font-size: inherit;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;

  .track {
    position: relative;
    display: block;
    height: 28px;
    border-radius: 7px;
    overflow: hidden;
  }

  .fill {
    position: absolute;
    inset: 0 auto 0 0;
    min-width: 3px;
    border-radius: 7px;
    opacity: 0.2;
    transition: opacity ${T.durBase} ${T.easeOut};
  }

  .name {
    position: relative;
    display: block;
    padding: 0 10px;
    font-size: 0.84em;
    font-weight: 500;
    line-height: 28px;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .value { font-size: 0.84em; font-weight: 500; letter-spacing: -0.015em; text-align: right; white-space: nowrap; }
  .share { font-size: 0.76em; color: ${text2}; text-align: right; }

  &:hover .fill, &:focus-visible .fill { opacity: 0.34; }
  &:focus-visible { outline: 2px solid ${borderStrong}; outline-offset: 2px; border-radius: 7px; }
`;
